import { DATA_POINTS, TYPES, SCHEMA_ORG, sellerProperty, acceptedPaymentMethods } from './constants';
import { buildOffer } from './OfferSchema';
import { birchReturnPolicy } from './ReturnPolicySchema';
import { toDollars, toCents } from '../../utils/utilities.mjs';
import { buildShippingByType } from './OfferShippingDetailSchema';

// Price helper utilities

const getActivePrice = (variant, variantPrices) => {
  const prices = variantPrices?.[variant.id];
  if (!prices) return null;
  return prices.sale > 0 ? prices.sale : prices.full;
};

export function deriveLowestPrice(variants = [], variantPrices = {}) {
  let lowPrice = Infinity;
  let inStockLowPrice = Infinity;

  variants.forEach(variant => {
    const price = getActivePrice(variant, variantPrices);
    if (typeof price === 'number' && price > 0) {
      if (variant.availableForSale && price < inStockLowPrice) inStockLowPrice = price;
      if (price < lowPrice) lowPrice = price;
    }
  });

  if (inStockLowPrice !== Infinity) {
    return inStockLowPrice;
  }
  if (lowPrice !== Infinity) {
    return lowPrice;
  }
  return undefined;
}

export function deriveHighestPrice(variants = [], variantPrices = {}) {
  let highPrice = -Infinity;

  variants.forEach(variant => {
    const price = getActivePrice(variant, variantPrices);
    if (typeof price === 'number' && price > 0 && price > highPrice) {
      highPrice = price;
    }
  });

  return highPrice !== -Infinity ? highPrice : undefined;
}

export function deriveGroupAvailability(variants = []) {
  return variants.some(v => v.availableForSale) ? SCHEMA_ORG.inStock : SCHEMA_ORG.outOfStock;
}

export function deriveInStockCount(variants = []) {
  return variants.filter(v => v.availableForSale).length;
}

export function buildAggregateOfferFromVariants(variants, options = {}) {
  const { slug, siteSettings = {}, variantPrices = {}, ...restOptions } = options;
  return buildAggregateOffer({ variants }, slug, siteSettings, variantPrices, restOptions);
}


// Lightweight offer summary builder — now uses unified buildOffer

// Main aggregate offer builder

export function buildAggregateOffer(product, slug, siteSettings, variantPrices = {}, options = {}) {
  if (!product?.variants || product.variants.length === 0) return undefined;

  const variants = product.variants;
  const currency = product.currency || DATA_POINTS.locale.currency;
  
  const lowPrice = options.lowPrice ?? deriveLowestPrice(variants, variantPrices);
  const highPrice = options.highPrice ?? deriveHighestPrice(variants, variantPrices);
  const availability = deriveGroupAvailability(variants);  

  const shippingDetails = (siteSettings.settings?.shipping ?? [])
    .map(s => buildShippingByType(s.type, s))
    .filter(Boolean);

  const offerSummaries = variants
    .map(variant => buildOffer(variant, product, variantPrices, slug, siteSettings, {
      includeInventoryLevel: true,
      includeEligibleQty: true,
      includePriceValidUntil: true,
      itemCondition: variant.condition || SCHEMA_ORG.newCondition,
    }))
    .filter(Boolean);

  const offerCount = options.offerCount ?? offerSummaries.length;

  if (lowPrice === undefined) return undefined;

  return {
    '@type': TYPES.aggregate_offer,
    '@id': `${DATA_POINTS.url.product}/${slug}#aggregateoffer`,
    lowPrice: toDollars(toCents(lowPrice)),
    highPrice: highPrice !== undefined ? toDollars(toCents(highPrice)) : undefined,
    priceCurrency: currency,
    ...(product.priceValidUntil && { priceValidUntil: product.priceValidUntil }),
    offerCount: offerCount,
    availability: availability,
    itemCondition: product.condition || SCHEMA_ORG.newCondition,
    ...sellerProperty,
    ...(product.maxOrderQuantity && {
      eligibleQuantity: {
        '@type': TYPES.quantitativeValue,
        minValue: product.minOrderQuantity ?? 1,
        maxValue: product.maxOrderQuantity
      }
    }),
    ...(product.shipping?.country && {
      areaServed: {
        '@type': TYPES.country,
        name: product.shipping.country === 'GB' ? 'United Kingdom' : product.shipping.country
      }
    }),

    hasMerchantReturnPolicy: birchReturnPolicy(product?.productType),
    ...(shippingDetails.length > 0 && { shippingDetails }),
    offers: offerSummaries,
    acceptedPaymentMethod: acceptedPaymentMethods,
    ...(product.offerValidFrom && { validFrom: product.offerValidFrom }),
    ...(product.offerValidThrough && { validThrough: product.offerValidThrough })
  };
}
